import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublicCms } from '@/api/cms';
import { getPublicSolutions } from '@/api/solution';
import { getPublicTools } from '@/api/tool';

interface HeroPageContent {
  badge: string;
  title: string;
  subtitle: string;
  desc: string;
  btn1: string;
  btn2: string;
  bgImage: string;
}

interface HeroStatConfig {
  id: string;
  target: number;
  suffix: string;
  label: string;
  sublabel: string;
  icon: string;
  color: string;
  autoMode?: boolean;
}

const EMPTY_HERO_PAGE: HeroPageContent = {
  badge: '',
  title: '',
  subtitle: '',
  desc: '',
  btn1: '',
  btn2: '',
  bgImage: '',
};

function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readNumber(value: unknown, fallback = 0): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return fallback;
  return value;
}

function normalizeHeroContent(raw: unknown): HeroPageContent {
  if (!raw || typeof raw !== 'object') return EMPTY_HERO_PAGE;
  const hero = raw as Record<string, unknown>;
  return {
    badge: readText(hero.badge),
    title: readText(hero.title),
    subtitle: readText(hero.subtitle),
    desc: readText(hero.desc),
    btn1: readText(hero.btn1),
    btn2: readText(hero.btn2),
    bgImage: readText(hero.bgImage),
  };
}

function normalizeHeroStats(raw: unknown): HeroStatConfig[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, idx): HeroStatConfig | null => {
      if (!item || typeof item !== 'object') return null;
      const stat = item as Record<string, unknown>;
      return {
        id: readText(stat.id) || `stat-${idx + 1}`,
        target: readNumber(stat.target, 0),
        suffix: readText(stat.suffix),
        label: readText(stat.label),
        sublabel: readText(stat.sublabel),
        icon: readText(stat.icon) || 'ri-bar-chart-box-line',
        color: readText(stat.color) || '#0ea5e9',
        autoMode: Boolean(stat.autoMode),
      } satisfies HeroStatConfig;
    })
    .filter((item): item is HeroStatConfig => Boolean(item));
}

interface CountUpProps {
  target: number;
  duration?: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
}

function CountUp({ target, duration = 2000, suffix = '', prefix = '', decimals = 0 }: CountUpProps) {
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCurrent(parseFloat((eased * target).toFixed(decimals)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, target, duration, decimals]);

  return (
    <span ref={ref}>
      {prefix}
      {decimals > 0 ? current.toFixed(decimals) : Math.floor(current)}
      {suffix}
    </span>
  );
}

export default function HeroSection() {
  const [titleVisible, setTitleVisible] = useState(false);
  const [heroContent, setHeroContent] = useState<HeroPageContent>(EMPTY_HERO_PAGE);
  const [stats, setStats] = useState<HeroStatConfig[]>([]);
  const [solutionsCount, setSolutionsCount] = useState(0);
  const [toolsCount, setToolsCount] = useState(0);
  const [contentError, setContentError] = useState<string | null>(null);

  const refreshHomeContent = useCallback(async () => {
    try {
      const cms = await getPublicCms('home_page_content');
      setHeroContent(normalizeHeroContent(cms?.hero));
      setStats(normalizeHeroStats(cms?.stats));
      setContentError(null);
    } catch (err) {
      setHeroContent(EMPTY_HERO_PAGE);
      setStats([]);
      setContentError(err instanceof Error ? err.message : '首页内容加载失败');
    }
  }, []);

  const refreshDynamicCounts = useCallback(async () => {
    try {
      const [solutionRes, toolRes] = await Promise.all([getPublicSolutions(), getPublicTools()]);
      const onlineSolutions = Array.isArray(solutionRes?.list)
        ? solutionRes.list.filter((item) => !item.offline).length
        : 0;
      const onlineTools = Array.isArray(toolRes?.list)
        ? toolRes.list.filter((item) => !item.offline).length
        : 0;
      setSolutionsCount(onlineSolutions);
      setToolsCount(onlineTools);
    } catch {
      setSolutionsCount(0);
      setToolsCount(0);
    }
  }, []);

  useEffect(() => {
    void refreshHomeContent();
    void refreshDynamicCounts();

    const handleContentRefresh = () => void refreshHomeContent();
    const handleDataRefresh = () => void refreshDynamicCounts();

    window.addEventListener('wanxiang_page_content_updated', handleContentRefresh);
    window.addEventListener('wanxiang_solutions_updated', handleDataRefresh);
    window.addEventListener('wanxiang_tools_updated', handleDataRefresh);

    return () => {
      window.removeEventListener('wanxiang_page_content_updated', handleContentRefresh);
      window.removeEventListener('wanxiang_solutions_updated', handleDataRefresh);
      window.removeEventListener('wanxiang_tools_updated', handleDataRefresh);
    };
  }, [refreshDynamicCounts, refreshHomeContent]);

  useEffect(() => {
    const timer = setTimeout(() => setTitleVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        {heroContent.bgImage ? (
          <img
            src={heroContent.bgImage}
            alt="hero background"
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#111827]"></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0ea5e9]/20 via-transparent to-[#f97316]/10"></div>
      </div>

      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#0ea5e9]/20 animate-pulse"
            style={{
              width: `${[80, 120, 60, 100, 70, 90][i]}px`,
              height: `${[80, 120, 60, 100, 70, 90][i]}px`,
              top: `${[15, 60, 30, 75, 45, 20][i]}%`,
              left: `${[10, 80, 50, 20, 70, 40][i]}%`,
              animationDelay: `${i * 0.8}s`,
              animationDuration: `${3 + i * 0.5}s`,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 w-full text-center px-6 max-w-5xl mx-auto">
        <div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/80 text-sm mb-8 transition-all duration-700"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(-12px)' }}
        >
          <span className="w-2 h-2 rounded-full bg-[#f97316] animate-pulse"></span>
          {heroContent.badge || '无'}
        </div>

        <h1
          className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight tracking-tight transition-all duration-700 delay-100"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          {heroContent.title.length > 2 ? heroContent.title.slice(0, 2) : heroContent.title || '无'}
          {heroContent.title.length > 2 && (
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8]">
              {heroContent.title.slice(2)}
            </span>
          )}
        </h1>

        <p
          className="text-xl md:text-2xl text-white/70 mb-4 font-light transition-all duration-700 delay-200"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          {heroContent.subtitle || '无'}
        </p>
        <p
          className="text-base text-white/50 mb-12 max-w-2xl mx-auto transition-all duration-700 delay-300"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          {heroContent.desc || '无'}
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4 justify-center transition-all duration-700 delay-500"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <Link
            to="/solutions"
            className="px-8 py-4 bg-[#0ea5e9] hover:bg-[#0284c7] text-white font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap text-base"
          >
            <i className="ri-compass-3-line mr-2"></i>
            {heroContent.btn1 || '无'}
          </Link>
          <Link
            to="/tools"
            className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-xl transition-all cursor-pointer whitespace-nowrap text-base"
          >
            <i className="ri-download-2-line mr-2"></i>
            {heroContent.btn2 || '无'}
          </Link>
        </div>

        <div
          className="mt-20 transition-all duration-700 delay-700"
          style={{ opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'translateY(0)' : 'translateY(30px)' }}
        >
          <div className="flex items-center gap-4 mb-10 max-w-xl mx-auto">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/20"></div>
            <span className="text-white/30 text-xs tracking-widest uppercase">核心数据</span>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/20"></div>
          </div>

          {contentError ? (
            <div className="max-w-3xl mx-auto rounded-2xl border border-red-200 bg-red-50/95 text-red-600 text-sm px-5 py-4">
              核心数据加载失败：{contentError}
            </div>
          ) : stats.length === 0 ? (
            <div className="max-w-3xl mx-auto rounded-2xl border border-white/20 bg-white/5 text-white/60 text-sm px-5 py-4">
              暂无核心数据
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {stats.map((stat, i) => {
                const target = stat.autoMode
                  ? stat.id === 'stat-solutions'
                    ? solutionsCount
                    : stat.id === 'stat-tools'
                      ? toolsCount
                      : 0
                  : stat.target;
                return (
                  <div
                    key={stat.id}
                    className="relative group"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 hover:border-white/20 transition-all duration-300 overflow-hidden">
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"
                        style={{ background: `radial-gradient(circle at 50% 0%, ${stat.color}20, transparent 70%)` }}
                      ></div>
                      <div
                        className="w-10 h-10 flex items-center justify-center rounded-xl mx-auto mb-3 transition-transform duration-300 group-hover:scale-110"
                        style={{ backgroundColor: `${stat.color}20` }}
                      >
                        <i className={`${stat.icon} text-lg`} style={{ color: stat.color }}></i>
                      </div>
                      <div
                        className="text-3xl md:text-4xl font-bold mb-1 tabular-nums"
                        style={{ color: stat.color }}
                      >
                        <CountUp target={target} duration={2200} suffix={stat.suffix} />
                      </div>
                      <div className="text-white font-semibold text-sm mb-1">{stat.label || '无'}</div>
                      <div className="text-white/40 text-xs leading-relaxed">{stat.sublabel || '无'}</div>
                      <div
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 w-0 group-hover:w-3/4 transition-all duration-300 rounded-full"
                        style={{ backgroundColor: stat.color }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-center gap-6 mt-8 flex-wrap">
            {[
              { icon: 'ri-open-source-line', text: '100% 开源' },
              { icon: 'ri-shield-check-line', text: '数据自主可控' },
              { icon: 'ri-community-line', text: '活跃社区支持' },
              { icon: 'ri-refresh-line', text: '持续更新维护' },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-1.5 text-white/40 text-xs">
                <i className={`${item.icon} text-sm text-white/30`}></i>
                {item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 animate-bounce">
        <span className="text-xs">向下滚动</span>
        <i className="ri-arrow-down-line"></i>
      </div>
    </section>
  );
}
